// Copyright © 2024 Ory Corp
// SPDX-License-Identifier: Apache-2.0

"use client"

import { ComponentPropsWithoutRef, ElementRef, forwardRef } from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { cn } from "../../utils/cn"

const DropdownMenu = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuContent = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 16, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    {/*
     * The `ory-elements` class re-establishes the scope that `postcss-scope`
     * applies to all utility classes. Without it, the portaled content lives
     * outside any `.ory-elements` ancestor and none of the Tailwind utilities
     * match. The inner `contents` wrapper is required because the scoped
     * `.ory-elements .contents` selector only matches descendants, and it
     * keeps the wrapped content out of the layout tree so Radix's popper
     * positioning is unaffected.
     */}
    <div className="ory-elements">
      <div className="contents">
        <DropdownMenuPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          align="end"
          className={cn(
            "z-50 min-w-76 origin-top-right animate-drop-down-in overflow-hidden will-change-[opacity,transform] data-[state=closed]:animate-drop-down-out",
            "rounded-cards border border-interface-border-default-primary bg-interface-background-default-primary",
            className,
          )}
          {...props}
        />
      </div>
    </div>
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Item>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-pointer items-center outline-hidden transition-colors select-none data-disabled:pointer-events-none",
      "gap-6 px-8 py-3 text-sm lg:py-4.5",
      "border-t border-button-secondary-border-default first:border-0 hover:border-button-social-border-hover",
      "bg-button-secondary-background-default text-button-secondary-foreground-default",
      "hover:bg-button-secondary-background-hover hover:text-button-secondary-foreground-hover",
      "data-[disabled]:bg-button-secondary-background-disabled data-[disabled]:text-button-secondary-foreground-disabled",
      inset && "pl-8",
      className,
    )}
    {...props}
  />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuLabel = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Label>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className,
    )}
    {...props}
  />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
}
